import { EXPLORE_ROUTES } from "../exploreRoutes";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";

import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../questionBank/QuestionBankList.css";
import "../quizzes/QuizLeaderboard.css";
import "./RapidFireLeaderBoard.css";
import HeaderProfileSection from "../HeaderProfileSection";
import MyWalletSidebar from "../MyWalletSidebar";
import { useRapidFireLeaderboard } from "../../hooks/useRapidFireLeaderboard";
import { useFilterStates } from "../../hooks/useFilterStates";
import { RAPID_FIRE_ROUTES } from "./rapidFireRoutes";
import {
  buildRapidFireFlowState,
  buildRapidFireSubjectsState,
  getStoredRapidFireFlowState,
  persistRapidFireFlowState,
} from "./rapidFireFlowState";

import leaderboardLeft from "../../assets/images/profile1.png";
import leaderboardCenter from "../../assets/images/expert1.png";
import leaderboardRight from "../../assets/images/c1.png";
import crown from "../../assets/images/crown1.png";

function getTopPlayerByRank(topThree = [], rank) {
  return topThree.find((player) => Number(player?.rank) === Number(rank)) || null;
}

function getTopPlayerBadge(rank) {
  if (rank === 1) return leaderboardCenter;
  if (rank === 2) return leaderboardLeft;
  return leaderboardRight;
}

function QuizLeaderboardShimmer() {
  return (
    <div className="leaderboard-screen" aria-hidden="true">
      <div className="leaderboard-header">
        <div className="event-info">
          <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__title" />
          <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__date" />
        </div>
      </div>
 
      <div className="leaderboard-content">
        <section className="leaderboard-card">
          <div className="top-three qbl-shimmer__topThree">
            {Array.from({ length: 3 }).map((_, index) => (
              <div className="top-player qbl-shimmer__topPlayer" key={index}>
                <span className="dashboard-skeleton dashboard-skeleton--thumb qbl-shimmer__topBadge" />
                <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__topName" />
                <span className="dashboard-skeleton dashboard-skeleton--button qbl-shimmer__topScore" />
              </div>
            ))}
          </div>

          <div className="rank-header">
            <span className="rank-heading">RANK LIST</span>
            <span className="performer-heading">Top Performers</span>
          </div>

          <div className="rank-list-wrapper">
            <div className="rank-list">
              {Array.from({ length: 10 }).map((_, index) => (
                <div className="rank-row qbl-shimmer__row" key={index}>
                  <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__rank" />
                  <span className="dashboard-skeleton dashboard-skeleton--thumb qbl-shimmer__avatar" />
                  <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__name" />
                  <span className="dashboard-skeleton dashboard-skeleton--button qbl-shimmer__score" />
                </div>
              ))}
            </div>

            <div className="rank-bottom-action">
              <div className="current-rank-card qbl-shimmer__currentCard">
                <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__rank" />
                <span className="dashboard-skeleton dashboard-skeleton--thumb qbl-shimmer__avatar" />
                <div className="current-rank-info">
                  <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__currentLabel" />
                  <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__currentName" />
                </div>
                <span className="dashboard-skeleton dashboard-skeleton--button qbl-shimmer__score" />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

const RapidFireLeaderBoard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const flowState = useMemo(
    () => buildRapidFireFlowState(location.state, getStoredRapidFireFlowState()),
    [location.state]
  );
  const [activeTab, setActiveTab] = useState("All Time");
  const tabs = ["All Time", "Monthly", "Today's"];
  const rankListRef = useRef(null);

  const goBack = () => {
    const backTarget = flowState?.rapidFireBackTarget;

    if (backTarget?.pathname) {
      navigate(backTarget.pathname, {
        replace: true,
        state: backTarget.state || {},
      });
      return;
    }

    if (Number(flowState?.subject_id || 0) > 0) {
      navigate(RAPID_FIRE_ROUTES.start, {
        replace: true,
        state: flowState,
      });
      return;
    }

    navigate(EXPLORE_ROUTES.subjects, {
      replace: true,
      state: buildRapidFireSubjectsState(flowState),
    });
  };

  const filterMapping = {
    "All Time": "all",
    "Monthly": "monthly",
    "Today's": "today",
  };

  const [selectedStateId, setSelectedStateId] = useState(0);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    persistRapidFireFlowState(flowState);
  }, [flowState]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    window.history.pushState({ rapidFireLeaderboardGuard: true }, "", window.location.href);

    const handlePopState = () => {
      goBack();
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [flowState]);

  const handleSelect = (state) => {
    setSelectedStateId(Number(state?.id || 0));
    setOpen(false);
  };

  const statesQuery = useFilterStates();
  const states = statesQuery.data || [];
  const selectedStateName =
    states.find((item) => Number(item.id) === Number(selectedStateId))?.name ||
    "Select State";

  const leaderboardQuery = useRapidFireLeaderboard({
    filter: filterMapping[activeTab] || "all",
    ...(selectedStateId > 0 ? { states_id: selectedStateId } : { states_id: 0 }),
  });

  const firstPage = leaderboardQuery.data?.pages?.[0] || null;

  const players = useMemo(() => {
    const pages = leaderboardQuery.data?.pages || [];
    const seenPlayers = new Set();

    return pages.flatMap((page) =>
      (page.players || []).filter((player) => {
        const uniqueKey = `${Number(player?.rank || 0)}-${Number(player?.studentId || 0)}`;
        if (seenPlayers.has(uniqueKey)) {
          return false;
        }
        seenPlayers.add(uniqueKey);
        return true;
      })
    );
  }, [leaderboardQuery.data]);

  const topThree = firstPage?.topThree || [];
  const firstPlayer = getTopPlayerByRank(topThree, 1);
  const secondPlayer = getTopPlayerByRank(topThree, 2);
  const thirdPlayer = getTopPlayerByRank(topThree, 3);
  const currentStudent = firstPage?.currentStudent || null;

  useEffect(() => {
    const container = rankListRef.current;
    if (!container) {
      return undefined;
    }

    const handleScroll = () => {
      if (!leaderboardQuery.hasNextPage || leaderboardQuery.isFetchingNextPage) {
        return;
      }

      const remainingHeight =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      if (remainingHeight < 120) {
        leaderboardQuery.fetchNextPage();
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [leaderboardQuery]);

  return (
    <>
      <style>{`
        .homeDashboard-wrapper .page-content {
          padding-right: 0;
          min-height: 0;
          height: calc(100vh - 35px);
          overflow-x: hidden;
        }

        .homeDashboard-wrapper .main-content {
          height: 100vh;
          overflow-x: hidden !important;
          box-sizing: border-box;
          padding-bottom: 0px;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }

        .layout-wrapper {
          background: #f8f9fa !important;
        }

        .page-padding {
          padding: 30px 25px 0 !important;
        }

       @media (max-width: 767px){
        .page-padding {
             padding: 30px 15px 0 !important;
        }
       }
      `}</style>
      <HomeDashboardLayout showHeader={false}>
        <div className="qbl-page qbankList-page page-padding fixed-page-btn rank-leader">
          <div className="start-quiz qbl-main instruction-main">
            <div className="hideProfile-page ">
              <div className="categories-page-header mb-0">
                <div>
                  <button
                    type="button"
                    className="categories-back-button"
                    onClick={goBack}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </div>
                <div>
                  <h4 className="d-flex categories-page-title"></h4>
                </div>
                <div>
                  <HeaderProfileSection />
                </div>
              </div>
            </div>

            <div className="leaderboard-screen">
               <div>
                <h1 className="qb-title">
                  Leaderboard
                </h1>

                <p className="qb-sub">
                  See how you rank among top learners.
                </p>
              </div>

              {leaderboardQuery.isLoading && !firstPage ? (
                <QuizLeaderboardShimmer />
              ) : (
                <>
                  <div className="leaderboard-header">
                    <div className="event-info">
                      <h1 className="event-title">Rapid Fire Leaderboard</h1>
                    </div>

                    <div className="state-dropdown" ref={dropdownRef}>
                      <button
                        type="button"
                        className={`state-dropdown-button ${open ? "dropdown-open" : ""}`}
                        onClick={() => setOpen((value) => !value)}
                      >
                        <span>{selectedStateName}</span>
                        <svg
                          className={`dropdown-chevron ${open ? "chevron-up" : ""}`}
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M6 9L12 15L18 9"
                            stroke="#68C900"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>

                      {open && (
                        <div className="state-dropdown-menu">
                          {states.map((state) => (
                            <button
                              type="button"
                              key={state.id}
                              className={`state-dropdown-option ${
                                Number(selectedStateId) === Number(state.id) ? "selected" : ""
                              }`}
                              onClick={() => handleSelect(state)}
                            >
                              {state.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="status-tabs">
                    {tabs.map((tab) => (
                      <button
                        key={tab}
                        className={`status-tab ${activeTab === tab ? "active" : ""}`}
                        onClick={() => setActiveTab(tab)}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  <div className="leaderboard-content">
                    <section className="leaderboard-card">
                      <div className="top-three">
                        {secondPlayer ? (
                          <div className="top-player second">
                            <div className="badge-wrapper">
                              <img
                                src={secondPlayer.avatar || getTopPlayerBadge(2)}
                                alt=""
                                className="top-badge"
                                onError={(e) => { e.target.src = getTopPlayerBadge(2); }}
                              />
                              <span className="rank-circle">2</span>
                            </div>
                            <div className="player-name">{secondPlayer.name}</div>
                            <div className="player-score">{secondPlayer.score}</div>
                          </div>
                        ) : (
                          <div className="top-player second" />
                        )}

                        {firstPlayer ? (
                          <div className="top-player first">
                               <span className="winner-crown">
                                    <img src={crown} alt="" />
                               </span>

                            <div className="badge-wrapper">
                              <img
                                src={firstPlayer.avatar || getTopPlayerBadge(1)}
                                alt=""
                                className="top-badge"
                                onError={(e) => { e.target.src = getTopPlayerBadge(1); }}
                              />
                              <span className="rank-circle">1</span>
                              {/* <span className="winner-star"><span>★</span></span> */}
                            </div>
                            <div className="player-name">{firstPlayer.name}</div>
                            <div className="player-score">{firstPlayer.score}</div>
                          </div>
                        ) : (
                          <div className="top-player first" />
                        )}

                        {thirdPlayer ? (
                          <div className="top-player third">
                            <div className="badge-wrapper">
                              <img
                                src={thirdPlayer.avatar || getTopPlayerBadge(3)}
                                alt=""
                                className="top-badge"
                                onError={(e) => { e.target.src = getTopPlayerBadge(3); }}
                              />
                              <span className="rank-circle grey">3</span>
                            </div>
                            <div className="player-name">{thirdPlayer.name}</div>
                            <div className="player-score">{thirdPlayer.score}</div>
                          </div>
                        ) : (
                          <div className="top-player third" />
                        )}
                      </div>

                    

                      <div className="rank-list-wrapper">
                          <div className="rank-header">
                        <span className="rank-heading">RANK LIST</span>
                        <span className="performer-heading">Top Performers</span>
                      </div>
                        <div className="rank-list" ref={rankListRef}>
                          {players.map((player) => (
                            <div className="rank-row" key={`${player.rank}-${player.studentId}`}>
                              <div className="rank-number">{player.rank}</div>
                              <img
                                className="small-badge"
                                src={player.avatar || leaderboardCenter}
                                alt=""
                                onError={(e) => { e.target.src = leaderboardCenter; }}
                              />
                              <div className="rank-name">{player.name}</div>
                              <div className="rank-score">{player.score}</div>
                            </div>
                          ))}
                          {leaderboardQuery.isFetchingNextPage && (
                            <div className="rank-row qbl-shimmer__row">
                              <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__rank" />
                              <span className="dashboard-skeleton dashboard-skeleton--thumb qbl-shimmer__avatar" />
                              <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__name" />
                              <span className="dashboard-skeleton dashboard-skeleton--button qbl-shimmer__score" />
                            </div>
                          )}
                        </div>

                        {currentStudent && (
                          <div className="rank-bottom-action">
                            <div className="current-rank-card">
                              <div className="current-rank-number">
                                {currentStudent.rank}
                              </div>
                              <div className="current-rank-avatar">
                                <img
                                  src={currentStudent.avatar || leaderboardCenter}
                                  alt=""
                                  onError={(e) => { e.target.src = leaderboardCenter; }}
                                />
                              </div>
                              <div className="current-rank-info">
                                <span>Your Current rank</span>
                                <strong>{currentStudent.name}</strong>
                              </div>
                              <div className="current-rank-score">
                                {currentStudent.score}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </section>
                  </div>
                </>
              )}
            </div>
          </div>

          <MyWalletSidebar withTopOffset={false} />
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default RapidFireLeaderBoard;
